<script setup lang="ts">
import { onBeforeUnmount, watch, ref } from 'vue';
import { useRouter } from 'vue-router';
import BankingWrapper from '@/application/layouts/ContentWrapper/BankingWrapper.vue';
import { throwableCall } from '@/api/lib/integration.ts';
import { notifyAccountCreated } from '@/api/banking/banksApi.ts';
import { captureException } from '@sentry/vue';
import { useTagManager } from '@demv_systems/feu-tag-manager';
import { TrackEventCategory } from '@/application/utils/tracking/events';
import { appUrl } from "@/application/utils/envInfo";

interface RegprotectElement extends HTMLElement {
  sessionLink?: string;
  reentryUrl?: string;
}

const props = defineProps<{
  sessionLink?: string;
  redirect?: string;
}>();

const router = useRouter();
let wcElement: RegprotectElement | undefined;

const regProtectContainerRef = ref<HTMLDivElement>();

async function handleWebComponentListener(event: Event) {
  const callbackUrl = (event as CustomEvent).detail;
  const url = new URL(callbackUrl);

  const baReentry = url.searchParams.get('baReentry');

  if (baReentry === 'ACCOUNT_CREATED') {
    try {
      await throwableCall(notifyAccountCreated())
      useTagManager().push({ event: TrackEventCategory.BANKS_API, action: 'ACCOUNT_CREATED' });
    } catch (err) {
      captureException(err);
    }
  }

  await router.push(`${url.pathname}?refresh=true`);
}

function cleanUpExistingWebComponent() {
  if (wcElement) {
    wcElement.removeEventListener('subscribed', handleWebComponentListener);
    wcElement.remove();
    wcElement = undefined;
  }
}

function createWebComponent() {
  if (!regProtectContainerRef.value) {
    throw new Error('regProtectContainerRef is not defined');
  }

  wcElement = document.createElement('ba-regprotect') as RegprotectElement;
  wcElement.sessionLink = props.sessionLink;
  wcElement.reentryUrl = `${appUrl}/banking/regprotect?redirect=${Date.now()}`;
  wcElement.addEventListener('subscribed', handleWebComponentListener);
  regProtectContainerRef.value.appendChild(wcElement);
}

watch(
  [() => props.redirect, regProtectContainerRef],
  ([_, newRegProtectContainerRef]) => {
    if (newRegProtectContainerRef) {
      cleanUpExistingWebComponent();
      createWebComponent();
    }
  },
  { flush: 'post', immediate: true }
);

onBeforeUnmount(() => {
  cleanUpExistingWebComponent()
})
</script>

<template>
  <BankingWrapper>
    <div id="regprotect" ref="regProtectContainerRef" class="h-full" />
  </BankingWrapper>
</template>
